import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { EmergencyCustomTemplate } from '@/types/api'

const EMPTY_TEMPLATES: EmergencyCustomTemplate[] = []

export type EmergencyTemplateScope = 'enable' | 'disable' | 'message'

export function templateScope(template: EmergencyCustomTemplate): EmergencyTemplateScope {
  return template.scope === 'disable' || template.scope === 'message' ? template.scope : 'enable'
}

export function prepareEmergencyTemplates(templates: EmergencyCustomTemplate[]): EmergencyCustomTemplate[] {
  return templates.map((template) => ({
    ...template,
    id: template.id.trim(),
    name: template.name.trim(),
    scope: templateScope(template),
  })).filter((template) => template.id && template.name)
}

/** Each form loads its own project's templates; late responses cannot cross projects. */
export function useEmergencyTemplates(projectId: string | null) {
  const [revision, setRevision] = useState(0)
  const [state, setState] = useState<{
    projectId: string | null
    templates: EmergencyCustomTemplate[]
    loading: boolean
    error: boolean
  }>({ projectId: null, templates: [], loading: false, error: false })
  const reload = useCallback(() => setRevision((value) => value + 1), [])

  useEffect(() => {
    if (!projectId) return
    let alive = true
    setState({ projectId, templates: [], loading: true, error: false })
    void api.emergencyCustomTemplates(projectId).then((templates) => {
      if (alive) setState({ projectId, templates: Array.isArray(templates) ? templates : [], loading: false, error: false })
    }).catch(() => {
      if (alive) setState({ projectId, templates: [], loading: false, error: true })
    })
    return () => { alive = false }
  }, [projectId, revision])

  const current = !!projectId && state.projectId === projectId
  return {
    templates: current ? state.templates : EMPTY_TEMPLATES,
    loading: !!projectId && (!current || state.loading),
    error: current && state.error,
    reload,
  }
}
